import {
  ReactNode,
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import useFramerMotion, {
  m,
  frame,
  cancelFrame,
  useSpring,
  useTransform,
  useReducedMotion,
  useInView,
  LazyMotion,
  type MotionValuePatch as MotionValue,
} from "@/hooks/useFramerMotion";

interface MarqueeProps {
  children: ReactNode;
  speed?: number;
  velocityFactor?: MotionValue<number>;
}

const Marquee: React.FC<MarqueeProps> = ({
  children,
  speed = 1,
  velocityFactor = null,
}) => {
  const framerMotionFeatures = useFramerMotion();
  const [repeatCount, setRepeatCount] = useState(2);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Observe the stationary clip, not the moving repeated content. Resume
  // slightly before it enters the viewport without unmounting its position.
  const isInView = useInView(containerRef, { margin: "100px 0px" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const speedBounds = useMemo(() => speed * 2.5, []);
  const baseSpeed = useSpring(1, {
    damping: 40,
    stiffness: 90,
    mass: 5,
  });
  const speedTransform = useTransform(() => speed);
  const springSpeed = useSpring(speedTransform, {
    damping: 30,
    stiffness: 70,
    mass: 5,
  });
  const tween = useTransform(() =>
    velocityFactor
      ? baseSpeed.get() * springSpeed.get() + velocityFactor.get() * 2
      : baseSpeed.get() * springSpeed.get()
  );
  const skewX = useTransform(
    tween,
    [speedBounds * -1, speedTransform.get(), speedBounds],
    [-20, speed * 2.5, 20]
  ) as MotionValue<number>;

  useEffect(() => {
    if (contentRef.current && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const singleContentWidth = contentRef.current.offsetWidth;
      const needed = Math.ceil(containerWidth / singleContentWidth) + 1;
      setRepeatCount(needed);
    }
  }, []);

  return (
    <LazyMotion features={framerMotionFeatures}>
      <div
        ref={containerRef}
        className="overflow-hidden">
        <m.div
          className="flex"
          style={{ x: "-25%", skewX }}>
          {repeatCount > 0 &&
            [...Array(repeatCount)].map((_, index) => (
              <MarqueeWrap
                ref={index === 0 ? contentRef : undefined}
                key={"marquee-wrap-" + index}
                active={isInView}
                tween={tween}>
                {children}
              </MarqueeWrap>
            ))}
        </m.div>
      </div>
    </LazyMotion>
  );
};

Marquee.displayName = "Marquee";

const MarqueeWrap = forwardRef(
  (
    {
      children,
      tween,
      active,
    }: {
      children: ReactNode;
      tween: MotionValue<number>;
      active: boolean;
    },
    fref
  ) => {
    const shouldReduceMotion = useReducedMotion();
    const item = useRef<HTMLDivElement>(null);
    const rect = useRef<DOMRect | null>(null);
    const x = useRef(0);
    useImperativeHandle(fref, () => item.current);

    useEffect(() => {
      if (!active || shouldReduceMotion) return;
      if (item.current) rect.current = item.current.getBoundingClientRect();

      const loop = () => {
        x.current -= tween.get();
        if (!item.current || !rect.current) return;
        let xPercentage = (x.current / rect.current.width) * 100;
        if (xPercentage < -100) {
          x.current = 0;
          xPercentage = 0;
        }
        if (xPercentage > 0) {
          x.current = -rect.current.width;
          xPercentage = -100;
        }
        item.current.style.transform = `translate3d(${xPercentage}%, 0, 0)`;
      };

      // Only committed instances own a loop; cancelled work cannot accumulate
      // across hover rerenders, route exits, or StrictMode effect replay.
      frame.update(loop, true);
      return () => cancelFrame(loop);
    }, [active, shouldReduceMotion, tween]);

    return (
      <div
        ref={item}
        className="inline-flex">
        {children}
      </div>
    );
  }
);

MarqueeWrap.displayName = "MarqueeWrap";

export default Marquee;
